import React, { useState } from 'react';
import { DragDropContext } from 'react-beautiful-dnd';
import { useSelector, useDispatch } from 'react-redux';

import { showToast } from '@/src/globalFunction';
import { RootState } from '@/src/reducers/types';
import { isJsonString } from '@/src/util/json';

import { useMoveFolder } from '../../api/moveFolder';
import { useLibraryListStore } from '../../store/library-list.store';

const Container = ({ render }) => {
  const files = useSelector((state: RootState) => state.files);
  const dispatch = useDispatch();
  const showUndoToast = useLibraryListStore((s) => s.showUndoToast);

  const [draggingData, setDraggingData] = useState({});
  const [draggingFromFiles, setDraggingFromFiles] = useState(false);
  const moveFolderMutation = useMoveFolder();

  const resetDragState = () => {
    setDraggingData({});
    setDraggingFromFiles(false);
  };

  const onDragEnd = async (result) => {
    const destination = result.destination;
    if (!destination) {
      resetDragState();
      return;
    }

    let newFolderId = undefined;

    if (destination.id === 'library' || destination.droppableId === 'library') {
      newFolderId = null;
    } else {
      newFolderId = +destination.droppableId;
    }

    const source = JSON.parse(result.source.droppableId);

    // Moving within file list
    if (isNaN(newFolderId)) {
      const destinationJson = JSON.parse(destination.droppableId);

      // Disable moving to non-folder
      if (destinationJson.source === 'document') {
        resetDragState();
        return;
      }

      newFolderId = isJsonString(destination.droppableId) ? destinationJson?.id : +newFolderId;

      // Prevent moving to self
      if (source.id === newFolderId) {
        resetDragState();
        return;
      }

      // Prevent moving folders to document
      if (destinationJson.source === 'document' && source.source === 'folder') {
        resetDragState();
        return;
      }
    }

    if (newFolderId === undefined) {
      resetDragState();
      return;
    }

    console.log('onDragEnd: moving folder', { source, destination }, 'into', newFolderId);

    const itemIdsVersioned = files.selectedItems.length
      ? files.selectedItems.map((f) => {
          return { id: f.id, version: f.version };
        })
      : [
          {
            id: +source.id,
            version: source.version,
          },
        ];

    try {
      const response = await moveFolderMutation.mutateAsync({
        itemIdsVersioned,
        parentId: destination.id === 'library' ? null : newFolderId,
      });

      if (response['error-results'].length > 0) {
        showToast('error', 'Something went wrong. Please try again later.');
        return;
      }

      if (response['conflict-results'].length > 0) {
        showToast('error', 'The document has already been upated. Please refresh and try again.');
        return;
      }

      if (itemIdsVersioned.length > 1) {
        dispatch({ type: 'RESET_SELECTED_ITEMS' });
      }

      const previousParentId = files.activeFolder.id;

      showUndoToast({
        itemIdsVersioned: response['success-results'].map((f) => ({
          id: f.id,
          version: f.version,
        })),
        previousParentId,
      });

      console.log('moveFolderMutation response', { response });
    } catch (err) {
      console.error('Failed to move folder', err);
    } finally {
      resetDragState();
    }
  };

  const onDragStart = (result) => {
    const sourceData = JSON.parse(result.source.droppableId);

    setDraggingData(sourceData);
    setDraggingFromFiles(true);
  };

  return (
    <div className="filesContentContainer">
      <DragDropContext onDragEnd={onDragEnd} onDragStart={onDragStart}>
        {render({ draggingData, draggingFromFiles })}
      </DragDropContext>
    </div>
  );
};

export default Container;
